import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import './NetworkTree.css';
import NetworkHeader from './components/NetworkHeader/NetworkHeader';
import ActivityLegend from './components/ActivityLegend/ActivityLegend';
import ZoomControls from './components/ZoomControls/ZoomControls';
import LoadingState from './components/LoadingState/LoadingState';
import MemberStatsModal from './MemberStatsModal';
import AddMemberModal from './AddMemberModal';
import { getNodeColor, getNodeSize, getNodeLabel, positionNodesAsTree } from './utils/nodeHelpers';
import { D3_SIMULATION } from './utils/constants';

function NetworkTree({ user, onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [networkData, setNetworkData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const simulationRef = useRef(null);
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const zoomBehaviorRef = useRef(null);

  useEffect(() => {
    fetchNetworkTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNetworkTree = async () => {
    try {
      const response = await fetch(`/backend/api/network/tree.php`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setNetworkData(data);
      }
    } catch (error) {
      console.error('Error fetching network tree:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize network when data loads
  useEffect(() => {
    if (networkData && containerRef.current) {
      initializeNetwork(networkData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkData]);

  // Prepare network data from raw data
  const prepareNetworkData = useMemo(() => (data) => {
    const nodes = [];
    
    data.nodes.forEach(node => {
      const colors = getNodeColor(node);
      const label = getNodeLabel(node);
      const nodeSize = getNodeSize(node.level);

      if (node.type === 'user') {
        // User nodes with circular profile pictures
        const nodeConfig = {
          id: node.id,
          label: label,
          level: node.level,
          type: node.type,
          shape: 'circularImage',
          image: `/assets/Profile/${node.profile_avatar || 'boy-1.svg'}`,
          size: nodeSize,
          color: colors,
          font: {
            color: '#f9fafb',
            size: 13,
            face: 'Inter, sans-serif',
            multi: 'html',
            bold: {
              color: '#ffffff'
            }
          },
          borderWidth: 4,
          borderWidthSelected: 5,
          shapeProperties: {
            useBorderWithImage: true
          },
          shadow: node.activity_score >= 80 ? {
            enabled: true,
            color: 'rgba(16, 185, 129, 0.5)',
            size: 15,
            x: 0,
            y: 0
          } : false
        };
        
        // Position root user at center and fix it in place
        if (node.level === 0) {
          nodeConfig.x = 0;
          nodeConfig.y = 0;
          nodeConfig.fixed = { x: true, y: true };  // Cannot be moved
        }
        // NO initial velocity - let physics handle everything
        
        nodes.push(nodeConfig);
      } else {
        // Temporary account nodes with profile picture
        nodes.push({
          id: node.id,
          label: label,
          level: node.level,
          type: node.type,
          shape: 'circularImage',
          image: '/assets/Profile/temp-usr.svg',
          size: nodeSize,
          color: colors,
          font: {
            color: '#d1d5db',
            size: 13,
            face: 'Inter, sans-serif'
          },
          borderWidth: 3,
          borderWidthSelected: 4,
          shapeProperties: {
            useBorderWithImage: true
          }
        });
      }
    });

    // Prepare edges with activity-based colors
    const edges = data.edges.map(edge => {
      let color;
      switch (edge.activity_level) {
        case 'high':
          color = '#10b981'; // green
          break;
        case 'medium':
          color = '#f59e0b'; // yellow/orange
          break;
        case 'low':
          color = '#ef4444'; // red
          break;
        case 'pending':
          color = '#9ca3af'; // gray for temporary accounts
          break;
        default:
          color = '#6b7280';
      }

      return {
        from: edge.from,
        to: edge.to,
        color: {
          color: color,
          highlight: color,
          hover: color
        },
        width: 2,
        smooth: {
          type: 'continuous',
          roundness: 0.5
        }
      };
    });

    return { nodes, edges };
  }, []);

  // D3.js drag behavior
  const drag = (simulation) => {
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }
    
    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }
    
    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      // Keep root node fixed
      if (d.level !== 0) {
        d.fx = null;
        d.fy = null;
      }
    }
    
    return d3.drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  };

  // Initialize D3.js force simulation network
  const initializeNetwork = (data) => {
    const { nodes, edges } = prepareNetworkData(data);
    
    // Clear any existing SVG
    d3.select(containerRef.current).selectAll("*").remove();
    
    // Get container dimensions
    const container = containerRef.current;
    const width = container.clientWidth || 1200;
    const height = container.clientHeight || 800;
    
    // Create SVG
    const svg = d3.select(containerRef.current)
      .append("svg")
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", [0, 0, width, height])
      .style("background", "transparent");
    
    svgRef.current = svg.node();
    
    // Convert edges to links format for D3
    const links = edges.map(edge => ({
      source: edge.from,
      target: edge.to,
      color: edge.color.color
    }));
    
    // Position nodes in inverted hierarchical tree layout (bottom to top)
    const requiredHeight = positionNodesAsTree(
      nodes,
      edges,
      width,
      height
    );
    
    // Update SVG viewBox to accommodate full tree height
    const finalHeight = Math.max(height, requiredHeight);
    svg.attr("viewBox", [0, 0, width, finalHeight]);
    
    // Simple force simulation - weak physics for gentle motion
    const simulation = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links)
        .id(d => d.id)
        .distance(D3_SIMULATION.LINK.DISTANCE)
        .strength(D3_SIMULATION.LINK.STRENGTH))
      .force("charge", d3.forceManyBody()
        .strength(D3_SIMULATION.CHARGE.STRENGTH))
      .force("collide", d3.forceCollide()
        .radius(d => d.size + D3_SIMULATION.COLLIDE.RADIUS_PADDING)
        .strength(D3_SIMULATION.COLLIDE.STRENGTH))
      .alpha(D3_SIMULATION.ALPHA)
      .alphaDecay(D3_SIMULATION.ALPHA_DECAY)
      .velocityDecay(D3_SIMULATION.VELOCITY_DECAY);
    
    // Pre-stabilize layout - run physics silently before rendering
    for (let i = 0; i < D3_SIMULATION.PRESTABILIZE_TICKS; i++) {
      simulation.tick();
    }
    simulation.alpha(D3_SIMULATION.ALPHA).restart();
    
    simulationRef.current = simulation;
    
    // Create links
    const link = svg.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter().append("line")
      .attr("stroke", d => d.color)
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.6);
    
    // Create node groups
    const node = svg.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(nodes)
      .enter().append("g")
      .call(drag(simulation))
      .on("click", (event, d) => {
        event.stopPropagation();
        const [type, id] = d.id.split('_');
        if (type === 'user') {
          setSelectedMember(`user_${id}`);
        } else if (type === 'temp') {
          setSelectedMember(`temp_${id}`);
        }
      });
    
    // Add circles to nodes
    node.append("circle")
      .attr("r", d => d.size)
      .attr("fill", d => d.color.background)
      .attr("stroke", d => d.color.border)
      .attr("stroke-width", d => d.borderWidth || 3);
    
    // Add images to nodes
    node.append("image")
      .attr("xlink:href", d => d.image)
      .attr("x", d => -d.size)
      .attr("y", d => -d.size)
      .attr("width", d => d.size * 2)
      .attr("height", d => d.size * 2)
      .attr("clip-path", d => `circle(${d.size}px at ${d.size}px ${d.size}px)`);
    
    // Add labels
    node.append("text")
      .text(d => d.label.split('\n')[0])
      .attr("text-anchor", "middle")
      .attr("dy", d => d.size + 15)
      .attr("fill", "#f9fafb")
      .attr("font-size", "12px")
      .attr("font-family", "Inter, sans-serif");
    
    // Update positions on each tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);
      
      node.attr("transform", d => `translate(${d.x},${d.y})`);
    });
    
    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        svg.selectAll(".nodes, .links").attr("transform", event.transform);
      });
    
    zoomBehaviorRef.current = zoom;
    svg.call(zoom);
  };

  const handleZoomIn = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleBy, 1.3);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition()
        .duration(300)
        .call(zoomBehaviorRef.current.scaleBy, 0.7);
    }
  }, []);

  const handleResetView = useCallback(() => {
    if (svgRef.current && zoomBehaviorRef.current) {
      const svg = d3.select(svgRef.current);
      svg.transition()
        .duration(500)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  const closeStatsModal = useCallback(() => {
    setSelectedMember(null);
  }, []);

  const handleNetworkRefresh = useCallback(() => {
    fetchNetworkTree();
  }, []);

  const handleMemberDelete = useCallback((memberId) => {
    if (!networkData) return;
    
    // 1. Get current SVG and simulation
    const svg = d3.select(svgRef.current);
    const simulation = simulationRef.current;
    
    // 2. Find the node to delete
    const nodeToDelete = svg.selectAll("g")
      .filter(d => d && d.id === memberId);
    
    if (nodeToDelete.empty()) return;
    
    // Get node data for particle explosion
    const nodeData = nodeToDelete.datum();
    const nodeX = nodeData.x;
    const nodeY = nodeData.y;
    
    // 3. Scale up bubble before burst
    nodeToDelete
      .transition()
      .duration(150)
      .ease(d3.easeBackOut.overshoot(2))
      .attr("transform", d => `translate(${d.x},${d.y}) scale(1.3)`)
      .style("opacity", 0.9)
      .on("end", () => {
        // 4. Create particle explosion
        const particleCount = 12; // Number of droplets
        const particles = [];
        
        for (let i = 0; i < particleCount; i++) {
          const angle = (Math.PI * 2 * i) / particleCount;
          particles.push({
            angle: angle,
            distance: 80 + Math.random() * 40, // Vary distance
            size: 4 + Math.random() * 6 // Random droplet sizes
          });
        }
        
        // 5. Create and animate particles
        svg.selectAll(".burst-particle")
          .data(particles)
          .enter()
          .append("circle")
          .attr("class", "burst-particle")
          .attr("cx", nodeX)
          .attr("cy", nodeY)
          .attr("r", d => d.size)
          .attr("fill", nodeData.color.background || "#3b82f6")
          .style("opacity", 1)
          .transition()
          .duration(600)
          .ease(d3.easeCubicOut)
          .attr("cx", d => nodeX + Math.cos(d.angle) * d.distance)
          .attr("cy", d => nodeY + Math.sin(d.angle) * d.distance)
          .attr("r", d => d.size * 0.3) // Shrink as they fly
          .style("opacity", 0)
          .remove();
        
        // 6. Remove the original node immediately
        nodeToDelete.remove();
      });
    
    // 7. Fade out connected links
    svg.selectAll("line")
      .filter(d => d && (d.source.id === memberId || d.target.id === memberId))
      .transition()
      .duration(300)
      .style("opacity", 0)
      .remove();
    
    // 8. Update simulation only - no React state change to avoid rebuild
    setTimeout(() => {
      // Update simulation nodes directly - nodes stay exactly where they are
      if (simulation) {
        const simNodes = simulation.nodes().filter(n => n.id !== memberId);
        simulation.nodes(simNodes);
        // No .restart() - everything stays in place
      }
      
      // DON'T call setNetworkData - it triggers full rebuild
      // DON'T call fetchNetworkTree - it also triggers rebuild
      // The backend deletion already happened, visual is updated, we're done!
      
    }, 750); // After burst animation completes
      
  }, [networkData]);

  const handleAddMember = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const closeMemberModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const handleMemberAdded = useCallback(() => {
    fetchNetworkTree();
  }, []);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="network-tree-container">
      <NetworkHeader 
        onNavigate={onNavigate}
        memberCount={networkData?.network_size}
        onAddMember={handleAddMember}
      />

      <div className="network-content">
        <div className="network-canvas" ref={containerRef}></div>

        <ZoomControls 
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetView={handleResetView}
        />

        <ActivityLegend />
      </div>

      {selectedMember && (
        <MemberStatsModal
          memberId={selectedMember}
          onClose={closeStatsModal}
          onRefresh={handleNetworkRefresh}
          onDelete={handleMemberDelete}
        />
      )}

      {showAddModal && (
        <AddMemberModal
          onClose={closeMemberModal}
          onMemberAdded={handleMemberAdded}
          networkNodes={networkData?.nodes || []}
        />
      )}
    </div>
  );
}

export default NetworkTree;
